import type { GameRules, GameSelection } from '@urbanrats/contracts';
import type { I18n, MessageKey } from '../i18n/index.ts';

/** Human condition for a selection under a round's locked rules (numbers explicit, never colour-only). */
export function conditionText(t: I18n['t'], rules: GameRules, sel: GameSelection): string {
  switch (rules.kind) {
    case 'COUNT_BANDS':
      return t(`cond.${sel}` as MessageKey, { low: rules.thresholds.low, high: rules.thresholds.high });
    case 'COUNT_BUCKETS': {
      const b = rules.buckets.find((x) => x.selection === sel);
      if (!b) return '';
      if (b.max === null) return t('cond.bucket.atLeast', { n: b.min });
      if (b.min === b.max) return t('cond.bucket.exactly', { n: b.min });
      return t('cond.bucket.between', { a: b.min, b: b.max });
    }
    case 'DUEL':
      return t(`cond.${sel}` as MessageKey);
    case 'FIRST_EVENT':
      return t(`cond.${sel}` as MessageKey);
  }
}

export const selectionLabel = (t: I18n['t'], sel: string) => t(`sel.${sel}` as MessageKey);

export const gameTitleKey = (id: string) => `game.${id}.title` as MessageKey;
export const gameBlurbKey = (id: string) => `game.${id}.blurb` as MessageKey;

/**
 * The counts a selection wins on, as plain numbers ("0–34", "36–40", "42+", "35 · 41"): what the option
 * shows first. Only for count-band games; null otherwise (the option then shows its name).
 */
export function winningCounts(rules: GameRules, sel: GameSelection): string | null {
  if (rules.kind !== 'COUNT_BANDS') return null;
  const { low, high } = rules.thresholds;
  switch (sel) {
    case 'UNDER':
      return low - 1 <= 0 ? '0' : `0–${low - 1}`;
    case 'RANGE':
      return high - low === 2 ? `${low + 1}` : `${low + 1}–${high - 1}`;
    case 'OVER':
      return `${high + 1}+`;
    case 'EXACT':
      return `${low} · ${high}`;
    case 'EXACT_LOW':
      return `${low}`;
    case 'EXACT_HIGH':
      return `${high}`;
    default:
      return null;
  }
}
